import { useState } from "react"
import { gregorianToHebrew, formatHebrewDate, getJewishHoliday, getParshaForShabbos, isYomTov } from "./hebrewCalendar"

const C = {
  navy:'#0f2237', steel:'#2d6a9f', text:'#1e2a3a', muted:'#6b7c93',
  border:'#e2e8f0', bg:'#f4f6f9'
}

const DOW_EN  = ['Sun','Mon','Tue','Wed','Thu','Fri','Shabbos']
const DOW_HE  = ['ראשון','שני','שלישי','רביעי','חמישי','שישי','שבת']
const MONTHS_EN = ['January','February','March','April','May','June','July','August','September','October','November','December']

function isSameDay(a,b) {
  return a.getFullYear()===b.getFullYear()&&a.getMonth()===b.getMonth()&&a.getDate()===b.getDate()
}

export default function CalendarView({ units, season, onQuickLog, currentUser }) {
  const today = new Date(); today.setHours(0,0,0,0)
  const [view, setView] = useState('month')
  const [current, setCurrent] = useState(new Date(today.getFullYear(), today.getMonth(), 1))
  const [selected, setSelected] = useState(today)

  // User calendar preferences with defaults
  const prefs = currentUser?.preferences || {}
  const showHebrew   = prefs.showHebrew   !== false  // default true
  const showParsha   = prefs.showParsha   !== false  // default true
  const showHolidays = prefs.showHolidays !== false  // default true
  const holidayLang  = prefs.holidayLang  || 'english' // default english
  const inEnglish    = holidayLang === 'english'

  // Get due date for a unit
  function getDueDate(u) {
    if(!u.last_changed) return null
    const interval = season==='winter' ? u.winter_interval : u.summer_interval
    if(!interval) return null
    const d = new Date(u.last_changed)
    d.setDate(d.getDate() + interval)
    d.setHours(0,0,0,0)
    return d
  }

  // Units due on a specific date
  function unitsDueOn(date) {
    return units.filter(u => {
      const due = getDueDate(u)
      if(!due) return false
      return isSameDay(due, date)
    })
  }

  // ── MONTH VIEW ────────────────────────────────────────────────
  function MonthView() {
    const year  = current.getFullYear()
    const month = current.getMonth()
    const firstDay = new Date(year, month, 1)
    // Week starts Sunday (0), shift so Sunday=0
    let startDow = firstDay.getDay() // 0=Sun,1=Mon,...,6=Sat
    const totalDays = new Date(year, month+1, 0).getDate()
    const cells = []

    // Empty cells before first day
    for(let i=0; i<startDow; i++) cells.push(null)
    for(let d=1; d<=totalDays; d++) cells.push(new Date(year, month, d))

    const hebMonth1 = gregorianToHebrew(year, month+1, 1)
    const hebMonthLabel = (() => {
      // Get Hebrew months spanning this Gregorian month
      const HMONTHS = ['ניסן','אייר','סיון','תמוז','אב','אלול','תשרי','חשון','כסלו','טבת','שבט','אדר','אדר ב׳']
      const hebEnd = gregorianToHebrew(year, month+1, totalDays)
      const m1 = HMONTHS[hebMonth1.month-1]
      const m2 = HMONTHS[hebEnd.month-1]
      return m1 === m2 ? m1 : `${m1} — ${m2}`
    })()

    return <>
      {/* Month header */}
      <div style={{textAlign:'center',marginBottom:12}}>
        <div style={{fontSize:20,fontWeight:800,color:C.navy}}>{MONTHS_EN[month]} {year}</div>
        {showHebrew&&<div style={{fontSize:13,color:C.muted,direction:'rtl',marginTop:2}}>{hebMonthLabel}</div>}
      </div>

      {/* Day of week headers */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2,marginBottom:4}}>
        {DOW_EN.map((d,i)=><div key={d} style={{textAlign:'center',padding:'4px 0'}}>
          <div style={{fontSize:10,fontWeight:700,color:i===6?'#1a5c9a':C.muted,textTransform:'uppercase'}}>{d}</div>
          {showHebrew&&<div style={{fontSize:9,color:'#9aacbe',direction:'rtl'}}>{DOW_HE[i]}</div>}
        </div>)}
      </div>

      {/* Calendar grid */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:3}}>
        {cells.map((day,i)=>{
          if(!day) return <div key={'e'+i}/>
          const isToday   = isSameDay(day, today)
          const isSelected= isSameDay(day, selected)
          const isShabbes = day.getDay() === 6
          const gy=day.getFullYear(), gm=day.getMonth()+1, gd=day.getDate()
          const holiday   = showHolidays ? getJewishHoliday(gy,gm,gd,inEnglish) : null
          const yomtov    = showHolidays ? isYomTov(gy,gm,gd) : false
          const parsha    = (showParsha && isShabbes) ? getParshaForShabbos(gy,gm,gd) : null
          const hebDate   = showHebrew ? formatHebrewDate(gy,gm,gd) : null
          const dayUnits  = unitsDueOn(day)
          const overdue   = dayUnits.filter(u=>day<today)
          const due       = dayUnits.filter(u=>day>=today)

          let bg = '#fff'
          if(isToday)    bg = C.navy
          else if(isSelected) bg = '#eef5fb'
          else if(yomtov) bg = '#fff8f0'
          else if(isShabbes) bg = '#fefdf5'

          return <div key={i} onClick={()=>setSelected(day)}
            style={{background:bg,border:`1.5px solid ${isToday?C.navy:isSelected?'#2d6a9f':yomtov?'#f0c070':C.border}`,
              borderRadius:8,padding:'5px 5px 4px',minHeight:74,cursor:'pointer',overflow:'hidden',transition:'all .12s'}}>
            {/* Date row */}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:2}}>
              <span style={{fontSize:13,fontWeight:700,color:isToday?'#fff':C.text}}>{gd}</span>
              {showHebrew&&hebDate&&<span style={{fontSize:9,color:isToday?'#7a9ab8':'#9aacbe',direction:'rtl',lineHeight:1.2,textAlign:'right',maxWidth:'60%'}}>{hebDate.split(' ').slice(0,1).join('')}</span>}
            </div>
            {/* Holiday */}
            {holiday&&<div style={{fontSize:8,fontWeight:700,color:'#c0392b',direction:inEnglish?'ltr':'rtl',lineHeight:1.3,marginBottom:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{holiday}</div>}
            {/* Parsha */}
            {parsha&&<div style={{fontSize:8,fontWeight:700,color:'#1a5c9a',direction:'rtl',background:'#eef5fb',borderRadius:3,padding:'1px 3px',marginBottom:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{parsha}</div>}
            {/* Unit badge */}
            {dayUnits.length>0&&<div style={{borderRadius:4,padding:'2px 4px',fontSize:8,fontWeight:700,color:'#fff',textAlign:'center',background:overdue.length>0?'#c0392b':due.length>0?'#d35400':'#1e8449',marginTop:1}}>
              {dayUnits.length} unit{dayUnits.length>1?'s':''}
            </div>}
          </div>
        })}
      </div>
    </>
  }

  // ── WEEK VIEW ─────────────────────────────────────────────────
  function WeekView() {
    // Find Sunday of current week
    const dow = current.getDay()
    const sunday = new Date(current); sunday.setDate(current.getDate() - dow)
    const days = Array.from({length:7},(_,i)=>{ const d=new Date(sunday); d.setDate(sunday.getDate()+i); return d })

    return <>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:4}}>
        {days.map((day,i)=>{
          const isToday   = isSameDay(day, today)
          const isSelected= isSameDay(day, selected)
          const isShabbes = day.getDay() === 6
          const gy=day.getFullYear(), gm=day.getMonth()+1, gd=day.getDate()
          const holiday   = showHolidays ? getJewishHoliday(gy,gm,gd,inEnglish) : null
          const parsha    = (showParsha && isShabbes) ? getParshaForShabbos(gy,gm,gd) : null
          const hebDate   = showHebrew ? formatHebrewDate(gy,gm,gd) : null
          const dayUnits  = unitsDueOn(day)

          return <div key={i} onClick={()=>setSelected(day)}
            style={{background:isToday?C.navy:isSelected?'#eef5fb':isShabbes?'#fefdf5':'#fff',
              border:`1.5px solid ${isToday?C.navy:isSelected?'#2d6a9f':C.border}`,
              borderRadius:10,padding:'8px 6px',minHeight:120,cursor:'pointer'}}>
            <div style={{textAlign:'center',marginBottom:6}}>
              <div style={{fontSize:10,fontWeight:700,color:isToday?'#7a9ab8':isShabbes?'#1a5c9a':C.muted,textTransform:'uppercase'}}>{DOW_EN[i]}</div>
              <div style={{fontSize:18,fontWeight:800,color:isToday?'#fff':C.text}}>{gd}</div>
              {showHebrew&&<div style={{fontSize:9,color:isToday?'#7a9ab8':'#9aacbe',direction:'rtl'}}>{hebDate?.split(' ').slice(0,1).join('')}</div>}
            </div>
            {holiday&&<div style={{fontSize:8,fontWeight:700,color:'#c0392b',direction:inEnglish?'ltr':'rtl',textAlign:'center',marginBottom:2,lineHeight:1.3}}>{holiday}</div>}
            {parsha&&<div style={{fontSize:8,fontWeight:700,color:'#1a5c9a',direction:'rtl',background:'#eef5fb',borderRadius:3,padding:'1px 4px',textAlign:'center',marginBottom:3}}>{parsha}</div>}
            <div style={{display:'flex',flexDirection:'column',gap:2}}>
              {dayUnits.slice(0,3).map(u=><div key={u.id+u.size} style={{fontSize:8,fontWeight:700,background:u.status.bg,color:u.status.color,borderRadius:3,padding:'2px 4px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{u.area}</div>)}
              {dayUnits.length>3&&<div style={{fontSize:8,fontWeight:700,color:'#fff',background:C.muted,borderRadius:3,padding:'2px 4px',textAlign:'center'}}>+{dayUnits.length-3}</div>}
            </div>
          </div>
        })}
      </div>
    </>
  }

  // ── SELECTED DAY DETAIL ───────────────────────────────────────
  const selUnits   = unitsDueOn(selected)
  const selGy      = selected.getFullYear()
  const selGm      = selected.getMonth()+1
  const selGd      = selected.getDate()
  const selHebDate = showHebrew ? formatHebrewDate(selGy,selGm,selGd) : null
  const selHoliday = showHolidays ? getJewishHoliday(selGy,selGm,selGd,inEnglish) : null
  const selParsha  = (showParsha && selected.getDay()===6) ? getParshaForShabbos(selGy,selGm,selGd) : null

  function navPrev() {
    if(view==='month') setCurrent(new Date(current.getFullYear(), current.getMonth()-1, 1))
    else { const d=new Date(current); d.setDate(d.getDate()-7); setCurrent(d) }
  }
  function navNext() {
    if(view==='month') setCurrent(new Date(current.getFullYear(), current.getMonth()+1, 1))
    else { const d=new Date(current); d.setDate(d.getDate()+7); setCurrent(d) }
  }

  const headerLabel = view==='month'
    ? `${MONTHS_EN[current.getMonth()]} ${current.getFullYear()}`
    : (() => { const dow=current.getDay(); const sun=new Date(current); sun.setDate(current.getDate()-dow); const sat=new Date(sun); sat.setDate(sun.getDate()+6); return `${MONTHS_EN[sun.getMonth()]} ${sun.getDate()} — ${MONTHS_EN[sat.getMonth()]} ${sat.getDate()}` })()

  return <div>
    {/* Header */}
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14,flexWrap:'wrap',gap:10}}>
      <div>
        <h1 style={{fontSize:20,fontWeight:800,color:C.text}}>Calendar</h1>
        <div style={{fontSize:11,color:C.muted,marginTop:2}}>Filter replacement schedule</div>
      </div>
      <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
        <button onClick={()=>{setCurrent(new Date(today.getFullYear(),today.getMonth(),1));setSelected(today)}}
          style={{border:`1px solid ${C.border}`,background:'#fff',borderRadius:8,padding:'6px 12px',fontSize:12,fontWeight:600,cursor:'pointer',color:C.muted}}>Today</button>
        <button onClick={navPrev} style={{border:`1px solid ${C.border}`,background:'#fff',borderRadius:8,padding:'6px 12px',cursor:'pointer',fontSize:14,color:C.muted}}>‹</button>
        <span style={{fontWeight:700,fontSize:13,color:C.text,minWidth:160,textAlign:'center'}}>{headerLabel}</span>
        <button onClick={navNext} style={{border:`1px solid ${C.border}`,background:'#fff',borderRadius:8,padding:'6px 12px',cursor:'pointer',fontSize:14,color:C.muted}}>›</button>
        <div style={{display:'flex',gap:2,background:'#f0f2f5',borderRadius:8,padding:3}}>
          {['month','week'].map(v=><button key={v} onClick={()=>setView(v)}
            style={{border:'none',background:view===v?C.navy:'transparent',color:view===v?'#fff':C.muted,borderRadius:6,padding:'5px 14px',fontSize:12,fontWeight:600,cursor:'pointer'}}>
            {v.charAt(0).toUpperCase()+v.slice(1)}
          </button>)}
        </div>
      </div>
    </div>

    {/* Calendar */}
    {view==='month' ? <MonthView/> : <WeekView/>}

    {/* Selected day detail */}
    <div style={{background:'#fff',borderRadius:12,marginTop:12,overflow:'hidden',boxShadow:'0 1px 4px rgba(0,0,0,.08)'}}>
      <div style={{background:C.navy,padding:'12px 16px',display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
        <div>
          <div style={{fontWeight:700,color:'#fff',fontSize:14}}>{selected.toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'})}</div>
          {showHebrew&&selHebDate&&<div style={{fontSize:11,color:'#7a9ab8',direction:'rtl',marginTop:2}}>{selHebDate}</div>}
          {selHoliday&&<div style={{fontSize:11,color:'#f0c070',marginTop:2,direction:inEnglish?'ltr':'rtl',fontWeight:700}}>{selHoliday}</div>}
          {selParsha&&<div style={{fontSize:11,color:'#4a9fd4',direction:'rtl',marginTop:1}}>פרשת {selParsha}</div>}
        </div>
        <div style={{textAlign:'right'}}>
          <div style={{color:'#fff',fontSize:12,fontWeight:600}}>{selUnits.length} unit{selUnits.length!==1?'s':''} due</div>
        </div>
      </div>
      {selUnits.length===0
        ?<div style={{padding:'20px 16px',textAlign:'center',color:C.muted,fontSize:13}}>No filter replacements due on this day.</div>
        :selUnits.map(u=>{
          const due=getDueDate(u)
          const days=due?Math.round((due-today)/(1000*60*60*24)):0
          return <div key={u.id+u.size} style={{padding:'12px 16px',borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div>
              <div style={{fontWeight:700,color:C.navy,fontSize:13}}>{u.area}</div>
              <div style={{fontSize:11,color:C.muted}}>📍 {u.location} · <span style={{fontFamily:'monospace'}}>{u.size}</span> × {u.qty}</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <span style={{background:u.status.bg,color:u.status.color,padding:'3px 9px',borderRadius:20,fontSize:11,fontWeight:700}}>
                {days<0?`${Math.abs(days)}d overdue`:days===0?'Today':`${days}d left`}
              </span>
              {onQuickLog&&<button onClick={()=>onQuickLog(u)} style={{background:C.navy,color:'#fff',border:'none',borderRadius:7,padding:'6px 14px',fontSize:12,fontWeight:700,cursor:'pointer'}}>Log</button>}
            </div>
          </div>
        })}
    </div>
  </div>
}
